// Shared contract: normalized values are used by storage, HTTP queries and the form.
export const FILTER_LISTS = {
  tags: 'Теги',
  districts: 'Районы',
  metroStations: 'Станции метро',
  features: 'Характеристики',
  propertyOptions: 'Опции свойств',
  contactTypes: 'Типы контактов',
  languages: 'Языки',
} as const;
export const FILTER_FLAGS = {
  new: 'Новая анкета',
  top: 'Топ',
  kyc: 'KYC',
  prepay: 'Предоплата',
} as const;
export const FILTER_PRICES = {
  price1h: { label: 'У себя · 1 час', field: 'priceDayOneHourUser' },
  price2h: { label: 'У себя · 2 часа', field: 'priceDayTwoHoursUser' },
  priceNight: { label: 'У себя · ночь', field: 'priceNightAllUser' },
  priceNight1h: { label: 'У себя · 1 час ночью', field: 'priceNightOneHourUser' },
  priceout1h: { label: 'С выездом · 1 час', field: 'priceDayOneHourCustomer' },
  priceout2h: { label: 'С выездом · 2 часа', field: 'priceDayTwoHoursCustomer' },
  priceoutNight: { label: 'С выездом · ночь', field: 'priceNightAllCustomer' },
  priceoutNight1h: { label: 'С выездом · 1 час ночью', field: 'priceNightOneHourCustomer' },
} as const;
export const LANGUAGE_CODES = [
  'en',
  'ru',
  'uk',
  'de',
  'fr',
  'es',
  'it',
  'pt',
  'pl',
  'ro',
  'nl',
  'cs',
  'bg',
  'sr',
  'hr',
  'tr',
  'ar',
  'fa',
  'zh',
  'hi',
  'ja',
  'ko',
];
export const LANGUAGE_LEVELS = ['a1', 'a2', 'b1', 'b2', 'c1', 'c2', 'native'];
export type FilterList = keyof typeof FILTER_LISTS;
export type FilterFlag = keyof typeof FILTER_FLAGS;
export type FilterPrice = keyof typeof FILTER_PRICES;
export interface FilterRange {
  min?: number;
  max?: number;
}
export type PublishingFilters = Partial<Record<FilterList, string[]>> &
  Partial<Record<FilterFlag, boolean>> & {
    properties?: Record<string, FilterRange>;
    prices?: Partial<Record<FilterPrice, FilterRange>>;
  };
export interface FilterOption {
  value: string;
  label: string;
}
export interface FilterGroup {
  key: string;
  label: string;
  options: FilterOption[];
  single?: boolean;
}
export interface FilterDirectory {
  lists: Record<FilterList, FilterOption[]>;
  features: FilterGroup[];
  propertyOptions: FilterGroup[];
  properties: Array<{ value: string; label: string; min: number | null; max: number | null }>;
  prices: Record<FilterPrice, { min: number | null; max: number | null }>;
}
export interface PublishingFilterDirectory extends FilterDirectory {
  sourceRevision: number;
  cityId: number;
  tags: string[];
}
export interface PublishingFilterPreview {
  total: number;
  sample: Array<{ id: number; name: string }>;
  checkedAt: number;
  sourceRevision: number;
}
// Source identifiers are opaque strings, including spaces; never rewrite them as URL slugs.
export const isFilterIdentifier = (s: string) =>
  s.length <= 100 &&
  s.trim().length > 0 &&
  !/[\p{Cc}\p{Cf}]/u.test(s) &&
  !['__proto__', 'constructor', 'prototype'].includes(s);
// Property ranges use the source grammar "identifier:min~max".
export const isFilterPropertyIdentifier = (s: string) => isFilterIdentifier(s) && !/[:~]/.test(s);
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('Фильтры должны быть объектом');
  return value as Record<string, unknown>;
}
function range(value: unknown): FilterRange | undefined {
  const r = object(value);
  if (Object.keys(r).some((k) => k !== 'min' && k !== 'max'))
    throw new Error('Неизвестная граница диапазона');
  for (const n of Object.values(r))
    if (typeof n !== 'number' || !Number.isFinite(n) || n < 0 || n > 1_000_000_000)
      throw new Error('Границы должны быть конечными неотрицательными числами');
  const { min, max } = r as FilterRange;
  if (min !== undefined && max !== undefined && min > max)
    throw new Error('Значение «от» не может быть больше «до»');
  return min === undefined && max === undefined
    ? undefined
    : { ...(min === undefined ? {} : { min }), ...(max === undefined ? {} : { max }) };
}
export function normalizeFilters(value: unknown = {}): PublishingFilters {
  const raw = object(value),
    result: Record<string, unknown> = {};
  const allowed = [
    ...Object.keys(FILTER_LISTS),
    ...Object.keys(FILTER_FLAGS),
    'properties',
    'prices',
  ];
  for (const key of Object.keys(raw).sort()) {
    if (!allowed.includes(key)) throw new Error(`Неизвестный фильтр: ${key}`);
    const v = raw[key];
    if (Object.hasOwn(FILTER_LISTS, key)) {
      const limit =
        key === 'contactTypes' || key === 'languages' ? 10 : key === 'propertyOptions' ? 30 : 100;
      if (
        !Array.isArray(v) ||
        v.length > limit ||
        v.some((s) => typeof s !== 'string' || !isFilterIdentifier(s))
      )
        throw new Error(
          `Некорректный список: ${FILTER_LISTS[key as FilterList]} (не более ${limit})`,
        );
      if (
        key === 'languages' &&
        v.some((s) => {
          const [code, level] = s.split('-');
          return (
            !LANGUAGE_CODES.includes(code) ||
            !LANGUAGE_LEVELS.includes(level) ||
            s !== `${code}-${level}`
          );
        })
      )
        throw new Error('Выберите язык и уровень из списка');
      if (v.length) result[key] = [...new Set(v)].sort();
    } else if (Object.hasOwn(FILTER_FLAGS, key)) {
      if (typeof v !== 'boolean') throw new Error('Флаг фильтра должен быть true или false');
      result[key] = v;
    } else {
      const entries = object(v),
        normalized: Record<string, FilterRange> = {};
      if (Object.keys(entries).length > 100) throw new Error('Слишком много диапазонов');
      for (const slug of Object.keys(entries).sort()) {
        if (
          !isFilterPropertyIdentifier(slug) ||
          (key === 'prices' && !Object.hasOwn(FILTER_PRICES, slug))
        )
          throw new Error('Неизвестный параметр диапазона');
        const r = range(entries[slug]);
        if (r) {
          if (
            key === 'properties' &&
            slug === 'age' &&
            Object.values(r).some((n) => !Number.isInteger(n) || n < 18 || n > 120)
          )
            throw new Error('Возраст должен быть целым числом от 18 до 120');
          normalized[slug] = r;
        }
      }
      if (Object.keys(normalized).length) result[key] = normalized;
    }
  }
  return result as PublishingFilters;
}
export const filtersKey = (filters: PublishingFilters) => JSON.stringify(normalizeFilters(filters));
export function appendFilterQuery(query: URLSearchParams, filters: PublishingFilters) {
  const f = normalizeFilters(filters);
  for (const key of Object.keys(FILTER_LISTS) as FilterList[])
    for (const value of f[key] ?? []) query.append(key, value);
  for (const key of Object.keys(FILTER_FLAGS) as FilterFlag[])
    if (f[key] !== undefined) query.set(key, String(f[key]));
  const encode = (r: FilterRange) => `${r.min ?? ''}~${r.max ?? ''}`;
  for (const [slug, r] of Object.entries(f.properties ?? {}))
    query.append('properties', `${slug}:${encode(r)}`);
  for (const [key, r] of Object.entries(f.prices ?? {})) query.set(key, encode(r));
}
export function filterProblems(filters: PublishingFilters, directory: FilterDirectory): string[] {
  const f = normalizeFilters(filters),
    errors: string[] = [];
  for (const key of Object.keys(FILTER_LISTS) as FilterList[]) {
    for (const value of f[key] ?? [])
      if (!directory.lists[key].some((o) => o.value === value))
        errors.push(`${FILTER_LISTS[key]}: ${value} — недоступно`);
  }
  for (const key of Object.keys(f.properties ?? {}))
    if (!directory.properties.some((p) => p.value === key))
      errors.push(`Свойство ${key} — недоступно`);
  for (const g of directory.features)
    if (g.single && g.options.filter((o) => f.features?.includes(o.value)).length > 1)
      errors.push(`${g.label}: выберите одно значение`);
  return errors;
}
export function summarizeFilters(filters: PublishingFilters, directory?: FilterDirectory): string {
  const parts: string[] = [];
  for (const key of Object.keys(FILTER_LISTS) as FilterList[])
    if (filters[key]?.length)
      parts.push(
        `${FILTER_LISTS[key]}: ${filters[key]!.map((v) => directory?.lists[key].find((o) => o.value === v)?.label ?? v).join(', ')}`,
      );
  for (const key of Object.keys(FILTER_FLAGS) as FilterFlag[])
    if (filters[key] !== undefined)
      parts.push(`${FILTER_FLAGS[key]}: ${filters[key] ? 'да' : 'нет'}`);
  const displayRange = (r: FilterRange) =>
    [r.min === undefined ? '' : `от ${r.min}`, r.max === undefined ? '' : `до ${r.max}`]
      .filter(Boolean)
      .join(' ');
  for (const [slug, r] of Object.entries(filters.properties ?? {}))
    if (r.min !== undefined || r.max !== undefined)
      parts.push(
        `${directory?.properties.find((p) => p.value === slug)?.label ?? (slug === 'age' ? 'Возраст' : slug)}: ${displayRange(r)}`,
      );
  for (const [key, r] of Object.entries(filters.prices ?? {}))
    if (r.min !== undefined || r.max !== undefined)
      parts.push(`${FILTER_PRICES[key as FilterPrice].label}: ${displayRange(r)}`);
  return parts.length ? parts.join('; ') : 'Фильтры не включены — все подходящие анкеты города.';
}
